import React, { useRef, useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import { AiModelPicker, AppButton, Callout, FormField, Input } from "../ui";
import useAiModels from "../../hooks/useAiModels";
import QuizBuilder from "../SelfPaced/QuizBuilder";
import { adminCreateQuickQuiz, adminGenerateQuizQuestions } from "../../utils/smartLearning";

const QUIZ_MAX = 20;

/**
 * Write a quiz (or edit one) with the self-paced quiz builder, optionally with
 * AI-drafted questions, and hand it back to be attached. The quiz is an
 * Assessment, so it needs the quiz plan feature (the picker checks).
 *
 * Props:
 *   courseId, context ({ moduleTitle, sessionTitle, sessionDescription }) — sent to the AI
 *   initialQuiz, submitFn, submitLabel — edit mode
 *   onCreated(resource), onCancel
 */
const QuizForm = ({
  courseId, context = {}, initialQuiz, submitFn = adminCreateQuickQuiz,
  submitLabel = "Create and attach", onCreated, onCancel,
}) => {
  const builderRef = useRef(null);
  const ai = useAiModels("/smart-learning/admin/quiz-ai-models");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");
  const [aiTopic, setAiTopic] = useState("");
  const [aiCount, setAiCount] = useState(5);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");

  // QuizBuilder reports its own validation through a toast callback.
  const setToast = ({ message, type } = {}) => {
    if (type === "error") setFailure(message || "Something went wrong.");
  };

  const generate = async () => {
    setAiError("");
    const existing = (builderRef.current?.getQuestions() || [])
      .map((q) => (q.question || "").trim())
      .filter(Boolean);
    if (existing.length >= QUIZ_MAX) {
      setAiError(`A quiz holds at most ${QUIZ_MAX} questions.`);
      return;
    }
    setAiBusy(true);
    try {
      const questions = await adminGenerateQuizQuestions({
        courseId,
        moduleTitle: context.moduleTitle,
        sessionTitle: context.sessionTitle,
        description: context.sessionDescription,
        topic: aiTopic.trim(),
        count: Math.min(QUIZ_MAX - existing.length, Number(aiCount) || 5),
        existingQuestions: existing,
        ...ai.payload,
      });
      if (questions?.length) {
        builderRef.current?.addQuestions(questions);
        if (context.sessionTitle && !builderRef.current?.getTitle?.()?.trim()) {
          builderRef.current?.setTitle?.(`${context.sessionTitle} — Quiz`);
        }
      } else {
        setAiError("No questions came back. Try a narrower topic.");
      }
    } catch (err) {
      setAiError(err?.response?.data?.message || "The AI could not write questions just now.");
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async () => {
    setFailure("");
    if (!builderRef.current) return;
    setSaving(true);
    try {
      const resource = await builderRef.current.submit();
      if (resource?._id) onCreated({ _id: resource._id, title: resource.title, kind: "quiz" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <section className="tw-flex tw-flex-col tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-sunken tw-p-3">
        <h4 className="tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-content">
          <Sparkles size={15} aria-hidden="true" className="tw-text-primary" /> Draft questions with AI
        </h4>
        <p className="tw-m-0 tw-text-xs tw-text-content-muted">
          Uses this {context.sessionTitle ? "session’s" : "topic’s"} title and description. Generate again for more — it won’t repeat
          a question already in the quiz. Up to {QUIZ_MAX}.
        </p>
        <div className="tw-grid tw-items-end tw-gap-2 min-[576px]:tw-grid-cols-[minmax(0,1fr)_6rem_auto]">
          <FormField label="Focus (optional)">
            <Input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="e.g. for-each loops" disabled={aiBusy} />
          </FormField>
          <FormField label="Questions">
            <Input type="number" min={1} max={QUIZ_MAX} value={aiCount} onChange={(e) => setAiCount(e.target.value)} disabled={aiBusy} />
          </FormField>
          <AppButton type="button" onClick={generate} disabled={aiBusy}>
            <Sparkles size={14} aria-hidden="true" /> {aiBusy ? "Writing…" : "Generate"}
          </AppButton>
        </div>
        <AiModelPicker {...ai} />
        {aiError && <Callout tone="danger" role="alert">{aiError}</Callout>}
      </section>

      {failure && <Callout tone="danger" role="alert">{failure}</Callout>}
      <QuizBuilder
        ref={builderRef}
        compact
        courseId={courseId}
        submitFn={submitFn}
        initialQuiz={initialQuiz}
        setToast={setToast}
      />
      <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-2">
        <AppButton type="button" variant="outline" onClick={onCancel} disabled={saving}>Cancel</AppButton>
        <AppButton type="button" onClick={submit} disabled={saving}>
          <Plus size={14} aria-hidden="true" /> {saving ? "Saving…" : submitLabel}
        </AppButton>
      </div>
    </div>
  );
};

export default QuizForm;
